import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {
  type TableConfig,
  type CellRange,
  tableAddRowAbove,
  tableAddRowBelow,
  tableAddColLeft,
  tableAddColRight,
  tableDeleteRows,
  tableDeleteCols,
  tableToggleHeaderRow,
  tableDuplicateRow,
  tableDuplicateCol,
  tableDistributeCols,
  tableDistributeRows,
  tableMergeCells,
  tableSplitCell,
  tableCellInsertElement,
  tableCellMoveElement,
  tableCellRemoveElement,
  createDefaultCellElement,
  cellSelection,
  canMerge,
  isSingleCell,
  normalizeRange,
} from './TableElement';

export interface TableContextMenuProps {
  x: number;
  y: number;
  ri: number;
  ci: number;
  cfg: TableConfig;
  onUpdate: (cfg: TableConfig) => void;
  onClose: () => void;
}

// ── Primitives ────────────────────────────────────────────────────────────────

function Sep() {
  return <div className="my-1 border-t border-border mx-1" />;
}

function Label({children}: {children: React.ReactNode}) {
  return (
    <p className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
      {children}
    </p>
  );
}

function Item({
  onClick,
  disabled,
  danger,
  checked,
  children,
}: {
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  checked?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`
        w-full flex items-center gap-2 px-3 py-[5px] text-left text-[13px] transition-colors
        ${danger ? 'hover:bg-destructive/10 hover:text-destructive' : 'hover:bg-accent'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-default'}
      `}
    >
      <span className="w-3.5 shrink-0 text-[10px] text-center leading-none select-none">
        {checked ? '✓' : ''}
      </span>
      {children}
    </button>
  );
}

// ── Menu ──────────────────────────────────────────────────────────────────────

export function TableContextMenu({x, y, ri, ci, cfg, onUpdate, onClose}: TableContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const run = (next: TableConfig) => {
    onUpdate(next);
    onClose();
  };

  const numRows = cfg.rows.length;
  const numCols = cfg.columns.length;
  const isHeader = !!cfg.rows[ri]?.isHeader;
  const anchorCell = cfg.rows[ri]?.cells[ci];
  /** The cell's element stack, and which of them the canvas has selected. */
  const {stack, index: elIdx} = cellSelection(cfg, ri, ci);
  const isMerged = !!anchorCell && ((anchorCell.rowSpan ?? 1) > 1 || (anchorCell.colSpan ?? 1) > 1);

  // Merge availability uses the current cellRange (may be a multi-cell selection).
  const normSel: CellRange | null = cfg.cellRange ? normalizeRange(cfg.cellRange) : null;
  const mergeable = normSel && !isSingleCell(normSel) && canMerge(cfg, normSel);

  // How many rows/columns the current selection spans — drives "Delete N rows/columns".
  const selRowCount = normSel ? normSel.endRow - normSel.startRow + 1 : 1;
  const selColCount = normSel ? normSel.endCol - normSel.startCol + 1 : 1;

  // Measure the rendered menu and flip it if it overflows the viewport.
  // Start hidden so the un-positioned flash is invisible.
  const [pos, setPos] = useState<{top: number; left: number} | null>(null);

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const {width, height} = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 6;

    // Horizontal: open right of click; flip left if it would overflow.
    let left = x;
    if (left + width + GAP > vw) left = x - width;
    left = Math.max(GAP, left);

    // Vertical: open below click; flip above if it would overflow.
    let top = y;
    if (top + height + GAP > vh) top = y - height;
    top = Math.max(GAP, top);

    // Position is derived from the rendered menu's measured size (flip on
    // viewport overflow), so the setState must happen after layout.

    setPos({top, left});
  }, [x, y]);

  return createPortal(
    <>
      {/* Backdrop: catches any click outside the menu via React's event system.
          stopPropagation prevents the click from bubbling to the canvas and
          accidentally deselecting the table element. */}
      <div
        style={{position: 'fixed', inset: 0, zIndex: 9998}}
        onMouseDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          top: pos?.top ?? y,
          left: pos?.left ?? x,
          zIndex: 9999,
          // Keep invisible until positioned to prevent a 1-frame flash at the wrong spot.
          visibility: pos ? 'visible' : 'hidden',
        }}
        className="bg-background border border-border rounded-lg shadow-xl py-1.5 w-52 text-sm"
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* ── Insert element ── */}
        <Label>Insert element</Label>
        <Item
          onClick={() =>
            run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('heading')))
          }
        >
          Text label
        </Item>
        <Item
          onClick={() => run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('text')))}
        >
          Text input
        </Item>
        <Item
          onClick={() =>
            run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('photo')))
          }
        >
          Image input
        </Item>
        <Item
          onClick={() => run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('logo')))}
        >
          Photo
        </Item>
        <Item
          onClick={() =>
            run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('checkbox')))
          }
        >
          Checkbox
        </Item>
        <Item
          onClick={() =>
            run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('dropdown')))
          }
        >
          Dropdown
        </Item>
        <Item
          onClick={() =>
            run(tableCellInsertElement(cfg, ri, ci, createDefaultCellElement('signature')))
          }
        >
          Signature
        </Item>
        {stack.length > 0 && (
          <Item onClick={() => run(tableCellRemoveElement(cfg, ri, ci))} danger>
            Remove all elements
          </Item>
        )}

        {/* ── Selected element ──
            Reordering lives here rather than on a drag grip beside the element.
            The grip had to reserve a gutter to exist, so selecting a cell that
            held two elements visibly narrowed both of them -- and a cell is the
            worst place in the editor to aim a drag, with the grip and the resize
            corner inside a box a couple of centimetres wide. A menu costs the
            element no room and needs no aim.

            Only when there is an order to change: one element has no position,
            and the section would otherwise be two permanently dead rows. */}
        {stack.length > 1 && (
          <>
            <Sep />
            <Label>Selected element</Label>
            <Item
              disabled={elIdx <= 0}
              onClick={() => run(tableCellMoveElement(cfg, ri, ci, elIdx, elIdx - 1))}
            >
              Move up
            </Item>
            <Item
              disabled={elIdx >= stack.length - 1}
              onClick={() => run(tableCellMoveElement(cfg, ri, ci, elIdx, elIdx + 1))}
            >
              Move down
            </Item>
          </>
        )}

        <Sep />

        {/* ── Row ── */}
        <Label>Row</Label>
        <Item onClick={() => run(tableAddRowAbove(cfg, ri))}>Add row above</Item>
        <Item onClick={() => run(tableAddRowBelow(cfg, ri))}>Add row below</Item>
        <Item onClick={() => run(tableDuplicateRow(cfg, ri))}>Duplicate row</Item>
        <Item onClick={() => run(tableToggleHeaderRow(cfg, ri))} checked={isHeader}>
          Header row
        </Item>
        <Item
          onClick={() => run(tableDeleteRows(cfg, normSel?.startRow ?? ri, normSel?.endRow ?? ri))}
          disabled={selRowCount >= numRows}
          danger
        >
          {selRowCount > 1 ? `Delete ${selRowCount} rows` : 'Delete row'}
        </Item>

        <Sep />

        {/* ── Column ── */}
        <Label>Column</Label>
        <Item onClick={() => run(tableAddColLeft(cfg, ci))}>Add column left</Item>
        <Item onClick={() => run(tableAddColRight(cfg, ci))}>Add column right</Item>
        <Item onClick={() => run(tableDuplicateCol(cfg, ci))}>Duplicate column</Item>
        <Item
          onClick={() => run(tableDeleteCols(cfg, normSel?.startCol ?? ci, normSel?.endCol ?? ci))}
          disabled={selColCount >= numCols}
          danger
        >
          {selColCount > 1 ? `Delete ${selColCount} columns` : 'Delete column'}
        </Item>

        {/* ── Merge / Split (conditional) ── */}
        {(mergeable || isMerged) && (
          <>
            <Sep />
            {mergeable && normSel && (
              <Item onClick={() => run(tableMergeCells(cfg, normSel))}>Merge cells</Item>
            )}
            {isMerged && <Item onClick={() => run(tableSplitCell(cfg, ri, ci))}>Split cell</Item>}
          </>
        )}

        <Sep />

        {/* ── Table ── */}
        <Label>Table</Label>
        <Item onClick={() => run(tableDistributeCols(cfg))}>Distribute columns evenly</Item>
        <Item onClick={() => run(tableDistributeRows(cfg))}>Distribute rows evenly</Item>
      </div>
    </>,
    document.body,
  );
}
